import { getDocumentReaderPaperViewUrl } from '@commenda/api/commenda-ai/document-reader/getDocumentReaderPaperViewUrl';
import { toast } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useState, type ReactNode } from 'react';

interface IDocumentReaderViewLinkProps {
  documentRef: string;
  className?: string;
  children: ReactNode;
}

/** A paper's "View" link in chat. The signed link is asked for on click and lives 10 minutes, so
 * the chat history never holds one that still works later. */
export function DocumentReaderViewLink({
  documentRef,
  className,
  children,
}: Readonly<IDocumentReaderViewLinkProps>) {
  const [isOpening, setIsOpening] = useState(false);

  const onClick = async () => {
    if (isOpening) return;
    // Opened before the request, so the browser counts it as part of the click and does not block it.
    const tab = window.open('', '_blank');
    setIsOpening(true);
    const result = await getDocumentReaderPaperViewUrl(documentRef);
    setIsOpening(false);
    const view = result.data?.view;
    if (!result.success || !view) {
      tab?.close();
      toast.error(result.error ?? 'Could not open this document.');
      return;
    }
    if (tab) {
      tab.opener = null;
      tab.location.href = view.url;
    } else window.open(view.url, '_blank', 'noopener,noreferrer');
    if (view.wholeFile)
      toast.success(
        'This document shares its file with other documents, so the whole file opened.'
      );
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isOpening}
      className={cn(
        'inline font-medium text-fg-brand underline underline-offset-2 hover:text-fg-brand/80 disabled:cursor-wait disabled:opacity-60',
        className
      )}
    >
      {children}
    </button>
  );
}
